import {
    ColDef,
    ColGroupDef,
    GetRowIdParams,
    IRowNode,
    ITooltipParams,
    PostSortRowsParams,
    RowClickedEvent,
    RowHeightParams,
    ValueFormatterParams,
    ValueGetterParams
} from "ag-grid-community";
import { AgGridReactProps } from "ag-grid-react";

const GROUP_HEADER = "agGridGroupHeader";

export const GROUP_HEADER_ROW_HEIGHT = 34;

/** The band rendered above each group. */
export interface GroupHeaderRow {
    kind: typeof GROUP_HEADER;
    groupKey: string;
    label: string;
    count: number;
}

/** What the grid's row model actually holds once grouping is on. */
export type GroupedRow<TData> = TData | GroupHeaderRow;

export interface RowGroup<TData> {
    /** Groups render in the order given here. */
    groups: ReadonlyArray<{ key: string; label: string }>;
    getGroupKey: (data: TData) => string;
}

export const isGroupHeaderRow = <TData extends object>(
    row: GroupedRow<TData> | undefined
): row is GroupHeaderRow =>
    row !== undefined && "kind" in row && row.kind === GROUP_HEADER;

/**
 * Grouping is only sound when every row lands in a declared group.
 */
const hasUnplaceableRow = <TData extends object>(
    rows: ReadonlyArray<TData | undefined>,
    rowGroup: RowGroup<TData>
): boolean => {
    const declaredKeys = new Set(rowGroup.groups.map((group) => group.key));
    return rows.some(
        (row) =>
            row === undefined || !declaredKeys.has(rowGroup.getGroupKey(row))
    );
};

/**
 * Interleave a header above each group.
 *
 * Falls back to an ungrouped table whenever the
 * grouping wouldn't be sound
 */
export const buildGroupedRows = <TData extends object>(
    data: ReadonlyArray<TData>,
    rowGroup: RowGroup<TData>
): GroupedRow<TData>[] => {
    if (hasUnplaceableRow(data, rowGroup)) {
        console.warn(
            "rowGroup: some rows have an undeclared group key; rendering the table ungrouped."
        );
        return [...data];
    }

    const populated = rowGroup.groups
        .map((group) => ({
            group,
            rows: data.filter((row) => rowGroup.getGroupKey(row) === group.key)
        }))
        .filter(({ rows }) => rows.length > 0);

    // A lone header spanning every row conveys no partition.
    if (populated.length < 2) {
        return [...data];
    }

    return populated.flatMap(({ group, rows }) => {
        const header: GroupHeaderRow = {
            kind: GROUP_HEADER,
            groupKey: group.key,
            label: group.label,
            count: rows.length
        };
        return [header, ...rows];
    });
};

/**
 * Reorder the row model so that each group is contiguous and
 *  within each group the rows are sorted by the given `sortGroup` function.
 */
const orderGroupedNodes = <TData extends object>(
    nodes: IRowNode<GroupedRow<TData>>[],
    rowGroup: RowGroup<TData>,
    sortGroup: (rows: IRowNode<GroupedRow<TData>>[]) => void
): void => {
    const rowData = nodes
        .map((node) => node.data)
        .filter((row): row is TData | undefined => !isGroupHeaderRow(row));
    if (hasUnplaceableRow(rowData, rowGroup)) {
        // treat as ungrouped table
        sortGroup(nodes);
        return;
    }

    const headers = new Map<string, IRowNode<GroupedRow<TData>>>();
    const rowsByGroup = new Map<string, IRowNode<GroupedRow<TData>>[]>();
    for (const node of nodes) {
        const row = node.data;
        if (isGroupHeaderRow<TData>(row)) {
            headers.set(row.groupKey, node);
            continue;
        }
        if (row === undefined) {
            continue;
        }
        const key = rowGroup.getGroupKey(row);
        const rows = rowsByGroup.get(key);
        if (rows === undefined) {
            rowsByGroup.set(key, [node]);
        } else {
            rows.push(node);
        }
    }

    const ordered = rowGroup.groups.flatMap((group) => {
        const header = headers.get(group.key);
        const rows = rowsByGroup.get(group.key);
        if (rows === undefined) {
            return header === undefined ? [] : [header];
        }
        sortGroup(rows);
        return header === undefined ? rows : [header, ...rows];
    });
    if (ordered.length === nodes.length) {
        for (let i = 0; i < nodes.length; i++) {
            nodes[i] = ordered[i];
        }
    }
};

type AnyColDef<TData> = ColDef<TData> | ColGroupDef<TData>;

const isColGroupDef = <TData>(
    def: AnyColDef<TData>
): def is ColGroupDef<TData> => "children" in def;

const asRowParams = <TParams>(params: unknown): TParams => params as TParams;

/**
 * We need to exclude group header rows from any column definitions so they don't break
 * the table
 */
const wrapColDef = <TData extends object>(
    colDef: ColDef<TData>
): ColDef<GroupedRow<TData>> => {
    const wrapped = {
        ...colDef
    } as unknown as ColDef<GroupedRow<TData>>;
    const { valueGetter, valueFormatter, comparator, tooltipValueGetter } =
        colDef;

    if (typeof valueGetter === "function") {
        wrapped.valueGetter = (params) =>
            isGroupHeaderRow<TData>(params.data)
                ? undefined
                : valueGetter(asRowParams<ValueGetterParams<TData>>(params));
    }
    if (typeof valueFormatter === "function") {
        wrapped.valueFormatter = (params) =>
            isGroupHeaderRow<TData>(params.data)
                ? ""
                : valueFormatter(
                      asRowParams<ValueFormatterParams<TData>>(params)
                  );
    }
    if (typeof comparator === "function") {
        wrapped.comparator = (a, b, nodeA, nodeB, isDescending) =>
            isGroupHeaderRow<TData>(nodeA.data) ||
            isGroupHeaderRow<TData>(nodeB.data)
                ? 0
                : comparator(
                      a,
                      b,
                      asRowParams<IRowNode<TData>>(nodeA),
                      asRowParams<IRowNode<TData>>(nodeB),
                      isDescending
                  );
    }
    if (typeof tooltipValueGetter === "function") {
        wrapped.tooltipValueGetter = (params) =>
            isGroupHeaderRow<TData>(params.data)
                ? undefined
                : tooltipValueGetter(
                      asRowParams<ITooltipParams<TData>>(params)
                  );
    }
    return wrapped;
};

const wrapColumnDefs = <TData extends object>(
    columnDefs: ReadonlyArray<AnyColDef<TData>>
): AnyColDef<GroupedRow<TData>>[] =>
    columnDefs.map((def) =>
        isColGroupDef(def)
            ? ({
                  ...def,
                  children: wrapColumnDefs(def.children)
              } as unknown as ColGroupDef<GroupedRow<TData>>)
            : wrapColDef(def)
    );

export const rowGroupingGridProps = <TData extends object>({
    rowGroup,
    rows,
    props
}: {
    rowGroup: RowGroup<TData>;
    rows: ReadonlyArray<TData>;
    props: AgGridReactProps<TData>;
}): AgGridReactProps<TData> => {
    const {
        getRowId,
        isRowSelectable,
        getRowHeight,
        postSortRows,
        onRowClicked
    } = props;

    const grouped: AgGridReactProps<GroupedRow<TData>> = {
        rowData: buildGroupedRows(rows, rowGroup),
        columnDefs: props.columnDefs
            ? wrapColumnDefs(props.columnDefs)
            : undefined,
        defaultColDef: props.defaultColDef
            ? wrapColDef(props.defaultColDef)
            : undefined,

        isFullWidthRow: (params) =>
            isGroupHeaderRow<TData>(params.rowNode.data),
        fullWidthCellRenderer: props.fullWidthCellRenderer,
        embedFullWidthRows: false,

        getRowHeight: (params) =>
            isGroupHeaderRow<TData>(params.data)
                ? GROUP_HEADER_ROW_HEIGHT
                : getRowHeight?.(asRowParams<RowHeightParams<TData>>(params)) ??
                  null,

        getRowId: (params) =>
            isGroupHeaderRow<TData>(params.data)
                ? `${GROUP_HEADER}:${params.data.groupKey}`
                : getRowId?.(asRowParams<GetRowIdParams<TData>>(params)) ?? "",

        onRowClicked: (event) => {
            if (!isGroupHeaderRow<TData>(event.data)) {
                onRowClicked?.(asRowParams<RowClickedEvent<TData>>(event));
            }
        },

        isRowSelectable: (node) =>
            isGroupHeaderRow<TData>(node.data)
                ? false
                : isRowSelectable?.(asRowParams<IRowNode<TData>>(node)) ?? true,
        defaultCsvExportParams: {
            ...props.defaultCsvExportParams,
            shouldRowBeSkipped: (params) =>
                isGroupHeaderRow<TData>(params.node.data)
        },

        postSortRows: (params) => {
            orderGroupedNodes(params.nodes, rowGroup, (rows) =>
                postSortRows?.(
                    asRowParams<PostSortRowsParams<TData>>({
                        ...params,
                        nodes: rows
                    })
                )
            );
        }
    };

    return grouped as unknown as AgGridReactProps<TData>;
};
